import { FC, useEffect } from 'react'

import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import s from './Step1.module.scss'

import { METAVERSE_IMAGE_BY_NAME } from 'constants/MetaverseIcons'
import { UUID_STRING } from 'constants/fieldsValidationValues'

import { useCreateMetaOpts } from 'hooks/useCreateMetaOpts'
import { useDownloadFile } from 'hooks/useDownloadFile'

import { useAppErrorContext } from 'contexts/AppErrorContext'
import { useData } from 'contexts/CreateAdSpotFormContext'

import { adSpotsApi } from 'api/adSpots-api'

import { GlobeIcon } from 'assets/icons/GlobeIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'
import { MoreIcon } from 'assets/icons/MoreIcon'

import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'
import { MetaverseNames } from 'types/Metaverse'

export const SURFACE_TYPE_BY_VALUE = {
  0: 'Billboard',
  1: 'Big Screen',
  2: 'Blue Neon Landscape',
  3: 'Blue Neon Portrait',
  100: 'Scene Files',
}

const surfaceTypes = [
  { value: -5, label: 'Surface type' },
  { value: 0, label: 'Billboard' },
  { value: 1, label: 'Big Screen' },
  { value: 2, label: 'Blue Neon Landscape' },
  { value: 3, label: 'Blue Neon Portrait' },
  { value: 100, label: 'Scene Files (SDK 6)' },
  { value: 101, label: 'Scene Files (SDK 7)' },
]

interface FormType {
  type: { value: number; label: string }
  metaverse: { value: string; label: string }
}

interface Step1Props {
  setActivePage: () => void
  onHide: () => void
}

let linkTutorial = 'https://github.com/MetaAdsTeam/advanced-metaads-decentraland-tutorial'

const Step1: FC<Step1Props> = ({ setActivePage, onHide }) => {
  const { setValues } = useData()
  const { setAppError } = useAppErrorContext()
  const [searchParams, setSearchParams] = useSearchParams()

  const { metaverseOptions } = useCreateMetaOpts(undefined, true)

  const { handleSubmit, control, watch } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      type: { value: -5, label: 'Surface type' },
      metaverse: metaverseOptions[0],
    },
  })

  const selectedMetaverse = watch('metaverse')
  const boardType = watch('type')

  const isNextButtonDisabled =
    (watch('type')?.value === -5 && selectedMetaverse?.value === 'Decentraland') || !selectedMetaverse?.value

  // Voxels UUID check
  const uuid = searchParams.get('uuid') || ''

  useEffect(() => {
    if (uuid) {
      if (!UUID_STRING.test(uuid)) {
        setAppError('Invalid UUID.')
      } else {
        setValues({
          metaverseType: 'Voxels',
          uuid: uuid,
        })
        setActivePage()
      }
      searchParams.delete('uuid')
      setSearchParams(searchParams)
    }
  }, [uuid])

  const [downloadRobloxAsset, robloxLoading] = useDownloadFile({
    getData: adSpotsApi.downloadRobloxAsset,
  })
  const [downloadUnityAsset, unityLoading] = useDownloadFile({
    getData: adSpotsApi.downloadUnityAsset,
  })
  const [downloadVoxelAsset, voxelLoading] = useDownloadFile({
    getData: adSpotsApi.downloadVoxelAsset,
  })

  const [downloadDecentralandAsset, decentralandLoading] = useDownloadFile({
    getData: () => adSpotsApi.downloadDecentralandAsset(boardType?.value),
    deps: [boardType?.value],
    fetchIf: +boardType?.value >= 0,
  })

  const onSubmit = (fData: FormType) => {
    setValues({
      type: fData?.type,
      metaverseType: (fData?.metaverse?.value as MetaverseNames),
    })
    setActivePage()
  }

  return (
    <ModalFullScreen onHide={onHide} className={s.modalWrapper} testid="createAdSpotStep1">
      <ModalFullScreen.Title>Create New Ad Space</ModalFullScreen.Title>
      <form onSubmit={handleSubmit(onSubmit)} className={s.wrapper} id="adspotFrom">
        <div className={s.formContentWrapper}>
          <div className={s.metaverseWrapper}>
            <div className={s.labelWrapper}>
              <h4 className={s.header}>Step 1</h4>
              <p className={s.description}>Choose the Metaverse</p>
            </div>
            <SelectControlled
              icon={<GlobeIcon />}
              optionsIcons={METAVERSE_IMAGE_BY_NAME}
              options={metaverseOptions}
              placeholder="Metaverse"
              control={control}
              name="metaverse"
              className={s.metaSelect}
            />
          </div>
          {selectedMetaverse?.value === 'Decentraland' ? (
            <>
              <h4 className={s.header}>Step 2</h4>
              <p className={s.description}>Choose desired Ad Space object type</p>
              <div className={s.selectsWrapper}>
                <SelectControlled
                  icon={<MoreIcon />}
                  options={surfaceTypes}
                  placeholder="Select surface type"
                  control={control}
                  name="type"
                  className={s.surfaceTypeSelect}
                />
              </div>
            </>
          ) : null}
          <div className={s.downloadWrapper}>
            {selectedMetaverse.value && (
              <div className={s.labelWrapper}>
                <h4 className={s.header}>Step {selectedMetaverse?.value === 'Decentraland' ? 3 : 2}</h4>
                <p className={s.description}>
                  Download the asset and upload it to desired metaverse{' '}
                  {selectedMetaverse?.value === 'Decentraland' ? (
                    <span>
                      {' '}
                      or <br /> use{' '}
                      <a href={linkTutorial} target="_blank" className={s.tutorialLink} rel="noreferrer">
                        Advanced MetaAds Tutorial
                      </a>
                    </span>
                  ) : null}
                </p>
              </div>
            )}
            {selectedMetaverse?.value === 'Voxels' ? (
              <CustomButton
                disabled={voxelLoading}
                variant="outlined"
                type="button"
                onClick={downloadVoxelAsset}
                size="180"
                className={s.downloadBtn}
                testid="voxel-download"
              >
                {voxelLoading ? 'Loading…' : 'Download'}
              </CustomButton>
            ) : selectedMetaverse?.value === 'Decentraland' ? (
              <CustomButton
                disabled={decentralandLoading || boardType?.value === -5}
                variant="outlined"
                type="button"
                onClick={downloadDecentralandAsset}
                size="180"
                className={s.downloadBtn}
                testid="dcl-download"
              >
                {decentralandLoading ? 'Loading…' : 'Download'}
              </CustomButton>
            ) : selectedMetaverse?.value === 'Unity' ? (
              <CustomButton
                disabled={unityLoading}
                variant="outlined"
                type="button"
                onClick={downloadUnityAsset}
                size="180"
                className={s.downloadBtn}
                testid="unity-download"
              >
                {unityLoading ? 'Loading…' : 'Download'}
              </CustomButton>
            ) : selectedMetaverse?.value === 'Roblox' ? (
              <CustomButton
                variant="outlined"
                type="button"
                onClick={downloadRobloxAsset}
                disabled={robloxLoading}
                size="180"
                testid="rbx-download"
                className={s.downloadBtn}
              >
                {robloxLoading ? 'Loading…' : 'Download'}
              </CustomButton>
            ) : null}
          </div>
          {selectedMetaverse?.value === 'Voxels' ? (
            <div className={s.voxelWarning} data-testid="Voxels-warn">
              <InfoIcon className={s.voxelWarnIcon} />
              <p className={s.description}>
                Firstly, you need to upload and setup the asset in Voxels. After that you can continue setting up the
                parameters here.
              </p>
            </div>
          ) : null}
        </div>
      </form>
      <div className={s.buttonsWrapper}>
        {selectedMetaverse?.value === 'Voxels' ? (
          <a href="https://www.voxels.com/" target="_blank" rel="noreferrer" className={s.voxelsLink}>
            <CustomButton type="button" variant="secondary" size="240">
              To Voxels
            </CustomButton>
          </a>
        ) : (
          <CustomButton
            type="submit"
            variant="secondary"
            disabled={isNextButtonDisabled}
            size="240"
            form="adspotFrom"
            testid="next-btn"
          >
            Next
          </CustomButton>
        )}
        <CustomButton type="button" variant="outlined" onClick={onHide} size="152">
          Close
        </CustomButton>
      </div>
    </ModalFullScreen>
  )
}

export default Step1
